import { ChangeEvent, MouseEvent, type SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import { Box, Button, Link, Stack, TextField, Typography } from '@mui/material';
import { Grid } from '@mui/system';
import { isEmpty, isEqual, isNull, isUndefined } from 'lodash';
import { useLocation, useParams } from 'react-router-dom';
import validate from 'validate.js';
import { CustomPrimaryUser } from 'api';
import { authAction, globalAction } from 'common/actions';
import { ERROR_403 } from 'common/config/api';
import {    APP_VERSION
} from 'common/config/app';
import * as messages from 'common/constants/messages/validations';
import {
  SEND_PASSWORD_RESET_EMAIL,
  USER_ACCESS_DENIED,
  USER_PROFILE_ERROR,
  USER_PROFILE_INFO_UPDATE,
} from 'common/constants/user';
import { userSchemaBase } from 'common/constants/userSchemaBase';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import { MessageBox } from 'components';
import FeatureFlagMenu from 'components/FeatureEnabler/feature-flag-menu/FeatureFlagMenu';
import { PixelCrop } from 'components/ImageContainer/utilities/types';
import ThemeModeControl from 'components/ThemeModeControl';
import ZoomControl from 'components/ZoomControl';
import { useSecondaryNavigationRegistration } from 'context/FirmBaseLayoutContext';
import { WORKBOARD_SLUG } from 'dashboard409a/common/constants';
import { useSendPasswordReset } from 'pages/Auth/hooks';
import { useFirmAssets, useImageUpload, useResponse, UsersHk } from 'services/hooks';
import { FirmAssetSignedUrlResponse } from 'services/hooks/useFirmAssets';
import { useGetUserDetails } from 'services/hooks/useUserSelected';
import ConnectedApps from './components/ConnectedApps';
import EmailsCheckbox from './components/EmailsCheckbox';
import { EmailNotificationOption } from './components/EmailsCheckbox.props';
import UserFormSkeleton from './components/UserFormSkeleton';
import { UserParams } from './types';
import useAccountSettingsNavItems from './useAccountSettingsNavItems';
import { UserProfileFormState } from './UserProfile.props';
import { userProfileClasses, UserProfileStyledGrid } from './UserProfile.styles';
import { USER_PROFILE_TEST_IDS } from './UserProfile.testIds';
import ImageContainer from '../../components/ImageContainer/ImageContainer';

const schema = userSchemaBase;

const getErrors = (values: UserProfileFormState['values']) => validate(values, schema);

const isValidValues = (values: UserProfileFormState['values']) => {
  const errors = getErrors(values);

  return isUndefined(errors) || isEmpty(errors);
};

const UserProfile = ({onSaveSuccess}: { onSaveSuccess?: (response: unknown) => void } = {}) => {
  const location = useLocation();
  const isWorkboard = location.pathname.includes(`/${WORKBOARD_SLUG}/`);
  const [isFeatureFlagMenuOpen, setIsFeatureFlagMenuOpen] = useState(false);
  const {userIdParam} = useParams() as Partial<UserParams>;
  const {useUpdateUserProfile} = UsersHk;
  const sendPasswordReset = useSendPasswordReset();
  const updateUserProfile = useUpdateUserProfile();
  const [{user, firmId}, dispatch] = useStore() as unknown as UseStoreValues;

  const userId = Number(userIdParam ?? user?.id ?? 0);
  const {data: userSelected, refetch: refetchUserDetails} = useGetUserDetails(userId);
  const [userInfo, setUserInfo] = useState<CustomPrimaryUser | null | string>(null);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<PixelCrop | null>(null);
  const [imageSrc, setImageSrc] = useState<string>('');
  const [originalImageSrc, setOriginalImageSrc] = useState('');
  const [isDirty, setIsDirty] = useState(false);
  const [formState, setFormState] = useState<UserProfileFormState>({
    isValid: false,
    values: {
      username: '',
      first_name: '',
      last_name: '',
      email: '',
      profile: {
        id: '',
        picture_url: '',
        receive_feature_emails: false,
        receive_document_emails: false,
      },
    },
    touched: {
      username: false,
      first_name: false,
      last_name: false,
      email: false,
      profile: {
        id: false,
        picture_url: false,
        receive_feature_emails: false,
        receive_document_emails: false,
      },
    },
    errors: {
      username: [],
      first_name: [],
      last_name: [],
      email: [],
      profile: {
        id: [],
        picture_url: [],
        receive_feature_emails: [],
        receive_document_emails: [],
      },
    },
  });
  const latestPixelCrop = useRef({});
  const {successNotification, errorNotification} = useResponse();
  const {getFirmAssetSignedUrl, uploadImageFile} = useFirmAssets();
  const {getCroppedImage} = useImageUpload();
  const [{isShowLoadingProgress}] = useStore() as unknown as UseStoreValues;

  const handleSetImageSrc = useCallback((value: SetStateAction<string | null>) => {
    setIsDirty(true);
    setImageSrc(currentValue => (typeof value === 'function' ? value(currentValue) ?? '' : value ?? ''));
  }, []);

  const handleResetPassword = useCallback(
    async (event: MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      if (formState.values.email) {
        const response = await sendPasswordReset(formState.values.email);
        if (isNull(response)) {
          // status code 204 => null body
          successNotification(messages.PASSWORD_CHANGE_SEND_MAIL);
        } else {
          errorNotification(messages.ERROR_OCCURRED);
        }
      }
    },
    [formState.values.email, sendPasswordReset, successNotification, errorNotification]
  );

  // Update ref for use in getCroppedImage
  useEffect(() => {
    if (croppedAreaPixels) {
      latestPixelCrop.current = croppedAreaPixels;
    }
  }, [croppedAreaPixels]);

  const saveUserProfileData = useCallback(async (valuesOverride?: UserProfileFormState['values']): Promise<void> => {
    const userInfoValue = userInfo as CustomPrimaryUser;
    const values = valuesOverride ?? formState.values;

    if (!formState.isValid || !userInfoValue?.id) {
      throw new Error('User profile save called with invalid form state');
    }

    const payload = { ...values };
    const [response, error] = await updateUserProfile(userInfoValue.id.toString(), payload);
    if (error) {
      if (error.status === 400) {
        errorNotification(messages.ERROR_OCCURRED);
        setFormState({
          ...formState,
          errors: error.response?.body ?? {},
          isValid: false,
        });
      }
      return Promise.reject(error);
    }

    if (!userIdParam || (userIdParam && user && user.id === Number.parseInt(userIdParam, 10))) {
      // Merge response with existing user to preserve permission fields (is_staff, is_superuser, etc.)
      // that are not returned by the profile update API endpoint
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      dispatch(authAction.setUserInfo({ ...(user as any), ...(response as any) }));
      onSaveSuccess?.(response);
    }
    setOriginalImageSrc(values.profile.picture_url ?? imageSrc);
    refetchUserDetails();
    successNotification(USER_PROFILE_INFO_UPDATE);
    setIsDirty(false);
  }, [dispatch, errorNotification, formState, imageSrc, onSaveSuccess, refetchUserDetails, successNotification, updateUserProfile, user, userIdParam, userInfo]);

  const uploadImageProfile = useCallback(async (): Promise<UserProfileFormState['values']> => {
    dispatch(globalAction.showLoadingProgress(true));
    try {
      const signedUrl = (await getFirmAssetSignedUrl(firmId as number, 'png')) as FirmAssetSignedUrlResponse;
      const file = await getCroppedImage(imageSrc, latestPixelCrop.current as PixelCrop);
      await uploadImageFile(signedUrl.url, file as File);
      const pictureUrl = signedUrl.public_url;
      const updatedValues = {
        ...formState.values,
        profile: {
          ...formState.values.profile,
          picture_url: pictureUrl,
        },
      };
      setFormState(current => ({
        ...current,
        values: updatedValues,
      }));
      return updatedValues;
    } catch (error) {
      errorNotification(error);
      return Promise.reject(error);
    } finally {
      dispatch(globalAction.showLoadingProgress(false));
    }
  }, [dispatch, errorNotification, firmId, formState.values, getCroppedImage, getFirmAssetSignedUrl, imageSrc, uploadImageFile]);

  const handleSaveButton = useCallback(async (): Promise<void> => {
    if (!formState.isValid) {
      return;
    }

    let valuesToSave = formState.values;
    if (imageSrc && imageSrc !== originalImageSrc) {
      valuesToSave = await uploadImageProfile();
    }

    await saveUserProfileData(valuesToSave);
  }, [formState.isValid, formState.values, imageSrc, originalImageSrc, saveUserProfileData, uploadImageProfile]);

  const handleSaveButtonRef = useRef(handleSaveButton);
  handleSaveButtonRef.current = handleSaveButton;

  // Stable onClick identity prevents FirmBaseLayout re-registration on every form edit.
  const handleSecondaryNavSave = useCallback(() => {
    handleSaveButtonRef.current();
  }, []);

  const accountSettingsNavItems = useAccountSettingsNavItems();

  const secondaryNavigation = useMemo(() => ({
    title: 'Account',
    items: accountSettingsNavItems,
    actions: [{
      type: 'button' as const,
      key: 'user-profile-save',
      label: 'Save',
      variant: 'positive' as const,
      onClick: handleSecondaryNavSave,
      disabled: !isDirty || !formState.isValid,
    }],
  }), [handleSecondaryNavSave, isDirty, formState.isValid, accountSettingsNavItems]);

  useSecondaryNavigationRegistration(secondaryNavigation);

  useEffect(() => {
    if (userInfo && userInfo !== ERROR_403) {
      const userInfoValue = userInfo as CustomPrimaryUser;
      const defaultImageUrl = userInfoValue.profile?.picture_url ?? '';
      const nextValues = {
        username: userInfoValue.username ?? '',
        first_name: userInfoValue.first_name ?? '',
        last_name: userInfoValue.last_name ?? '',
        email: userInfoValue.email ?? '',
        profile: {
          id: userInfoValue.profile?.id?.toString() ?? '',
          picture_url: defaultImageUrl,
          receive_feature_emails: userInfoValue.profile?.receive_feature_emails ?? false,
          receive_document_emails: userInfoValue.profile?.receive_document_emails ?? false,
        },
      };

      setFormState(formStateData => {
        // Query refetches can reuse equivalent profile data and should not reset dirty form state.
        if (isEqual(formStateData.values, nextValues)) {
          return formStateData;
        }

        return {
          ...formStateData,
          values: nextValues,
        };
      });
      setImageSrc(currentImageSrc => (currentImageSrc === defaultImageUrl ? currentImageSrc : defaultImageUrl));
      setOriginalImageSrc(currentImageSrc => (currentImageSrc === defaultImageUrl ? currentImageSrc : defaultImageUrl));
    }
  }, [userInfo]);

  useEffect(() => {
    if (userSelected) {
      // TanStack Query can hand back a new reference for unchanged data and retrigger hydration effects.
      setUserInfo(currentUserInfo => {
        if (typeof currentUserInfo === 'object' && currentUserInfo !== null && isEqual(currentUserInfo, userSelected)) {
          return currentUserInfo;
        }
        return userSelected;
      });
    }
  }, [userSelected]);

  useEffect(() => {
    setFormState(formStateData => {
      const values = {
        ...formStateData.values,
        profile: {
          ...formStateData.values.profile,
          picture_url: imageSrc,
        },
      };
      const isValid = isValidValues(values);

      if (formStateData.values.profile.picture_url === imageSrc && formStateData.isValid === isValid) {
        return formStateData;
      }

      return {
        ...formStateData,
        values,
        isValid,
      };
    });
  }, [imageSrc]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    event.persist();
    setIsDirty(true);
    const key = event.target.name;
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;

    // Check if this is a profile field
    const isProfileField = key === 'receive_feature_emails' || key === 'receive_document_emails';

    setFormState(formStateData => {
      const values = {
        ...formStateData.values,
        ...(isProfileField
          ? {
            profile: {
              ...formStateData.values.profile,
              [key]: value,
            },
          }
          : {
            [key]: value,
          }),
      };
      const errors = getErrors(values);

      return {
        ...formStateData,
        values,
        touched: {
          ...formStateData.touched,
          ...(isProfileField
            ? {
              profile: {
                ...formStateData.touched.profile,
                [key]: true,
              },
            }
            : {
              [key]: true,
            }),
        },
        isValid: isValidValues(values),
        errors: errors ?? formStateData.errors,
      };
    });
  };

  const hasError = (field: keyof UserProfileFormState['errors']) =>
    formState.touched[field] && (formState.errors[field] as string[]).length > 0;

  if (isNull(userInfo)) {
    return <UserFormSkeleton/>;
  }

  if (userInfo === ERROR_403) {
    return <MessageBox title={USER_ACCESS_DENIED} fullWidth={false}/>;
  }

  if (isUndefined(userInfo)) {
    return <MessageBox title={USER_PROFILE_ERROR} fullWidth={false}/>;
  }

  const emailNotificationOptions: EmailNotificationOption[] = [
    {
      name: 'receive_feature_emails',
      label: 'Receive email notifications of new features',
      ariaLabel: 'Receive email notifications of new features',
      dataTestId: USER_PROFILE_TEST_IDS.receiveFeatureEmailsCheckbox,
      checked: formState.values.profile.receive_feature_emails,
      onChange: handleChange,
    },
    {
      name: 'receive_document_emails',
      label: 'Receive email notifications of all document uploads',
      ariaLabel: 'Receive email notifications of all document uploads',
      dataTestId: USER_PROFILE_TEST_IDS.receiveDocumentEmailsCheckbox,
      checked: formState.values.profile.receive_document_emails,
      onChange: handleChange,
    },
  ];

  return (
    <UserProfileStyledGrid container spacing={2}>
      <Grid size={{xs: 12, md: 6}}>
        <Typography variant="h6">Personal Data</Typography>
        <br/>
        <Grid container spacing={4}>
          <Grid size={6}>
            <TextField
              className={userProfileClasses.textField}
              error={hasError('first_name')}
              fullWidth
              helperText={hasError('first_name') ? formState.errors.first_name[0] : null}
              label="First name"
              name="first_name"
              variant="outlined"
              onChange={handleChange}
              type="text"
              value={formState.values.first_name}
              slotProps={{htmlInput: {'aria-label': 'first_name', 'data-testid': USER_PROFILE_TEST_IDS.firstNameInput}}}
              id="first_name"
            />
          </Grid>
          <Grid size={6}>
            <TextField
              className={userProfileClasses.textField}
              error={hasError('last_name')}
              fullWidth
              helperText={hasError('last_name') ? formState.errors.last_name[0] : null}
              label="Last name"
              name="last_name"
              variant="outlined"
              onChange={handleChange}
              type="text"
              value={formState.values.last_name}
              slotProps={{htmlInput: {'aria-label': 'last_name', 'data-testid': USER_PROFILE_TEST_IDS.lastNameInput}}}
              id="last_name"
            />
          </Grid>
        </Grid>
        <br/>
        <br/>
        <Grid container spacing={2}>
          <Grid size={6}>
            <TextField
              className={userProfileClasses.textField}
              error={hasError('email')}
              fullWidth
              helperText={hasError('email') ? formState.errors.email[0] : null}
              label="Email"
              name="email"
              variant="outlined"
              onChange={handleChange}
              type="text"
              value={formState.values.email}
              slotProps={{htmlInput: {'aria-label': 'email', 'data-testid': USER_PROFILE_TEST_IDS.emailInput}}}
              id="email"
            />
          </Grid>
        </Grid>
        <br/>
        <Button onClick={handleResetPassword} variant="outlined" color="primary"
          data-testid={USER_PROFILE_TEST_IDS.resetPasswordButton}
          disabled={isShowLoadingProgress}>
          <EmailOutlinedIcon className={userProfileClasses.emailIconStyle}/> {SEND_PASSWORD_RESET_EMAIL}
        </Button>
        <br/>
        <EmailsCheckbox options={emailNotificationOptions}/>
      </Grid>
      <Grid size={{xs: 12, md: 6}}>
        <ImageContainer
          imageSrc={imageSrc}
          setImageSrc={handleSetImageSrc}
          setCroppedAreaPixels={setCroppedAreaPixels}
        />
      </Grid>
      <Grid size={12}>
        <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-start'}}>
          <ZoomControl/>
          {isWorkboard && <ThemeModeControl/>}
        </Box>
      </Grid>
      <Grid size={12}>
        <ConnectedApps />
      </Grid>
      <Grid size={12}>
        <Box sx={{ mt: 2, color: 'text.secondary' }}>
          <Stack direction="row">
            <Typography variant="caption">
              {'© '}
              <Link href="https://scalar.io/" target="_blank" underline="hover" color="inherit">
                Scalar Technologies
              </Link>
              {` ${new Date().getFullYear()} `}
              <Box component="span" sx={{ mx: 1, color: 'silver' }}>|</Box>
              {APP_VERSION && APP_VERSION.trim()[0].toUpperCase() === 'V' ? '' : 'v'}
            </Typography>
            <Typography
              variant="caption"
              onDoubleClick={() => setIsFeatureFlagMenuOpen(true)}
              sx={{ userSelect: 'none', cursor: 'default' }}>
              {APP_VERSION}
            </Typography>
          </Stack>
        </Box>
      </Grid>
      <FeatureFlagMenu open={isFeatureFlagMenuOpen} onClose={() => setIsFeatureFlagMenuOpen(false)} />
    </UserProfileStyledGrid>
  );
};

export default UserProfile;
